// ChatMarkdown.tsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import remarkBreaks from 'remark-breaks';
import rehypeKatex from 'rehype-katex';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import mermaid from 'mermaid';
import rehypeRaw from 'rehype-raw';
import html2canvas from 'html2canvas';

// Add this import near the top of the file if you don’t already have it:
import { CheckCircle2, ChevronDown, ChevronUp, Circle, ListTodo, Loader2, Wrench } from 'lucide-react';

type StatusData = {
	doneCount: number;
	pct: number;
	hasTasks: boolean;
	hasTools: boolean;
	hasStatus: boolean;
	activeTaskIdx: number;
	anyToolRunning: boolean;
	isWorking: boolean;
	tasks: TaskItem[];
	tools: ToolEvent[];
};

type AskQuestionData = {
	question: string;
	options: string[];
};

type TaskItem = { label: string; done: boolean };
type ToolEvent = { id: string; name: string; status: 'running' | 'done' };

type MatrixLoaderProps = {
	rows?: number;
	cols?: number;
	size?: number;
	gap?: number;
	activeCount?: number;
	interval?: number;
	className?: string;
	activeColor?: string;
};

// Font size keys - defined separately to avoid circular type reference
type FontSize = 'sm' | 'base' | 'lg';

const FONT_SIZE_MAP: Record<FontSize, string> = {
	sm: '0.875rem',
	base: '1rem',
	lg: '1.125rem',
};

const META_BLOCK_TAGS = new Set([
	'task_progress',
	'attempt_completion',
	'ask_followup_question',
	'switch_facility',
	'tool_response',
]);
const META_INLINE_TAGS = new Set(['result']);
const WAITING_SPINNER_LABELS = ['Preparing-task', 'Gathering-context', 'Checking-progress', 'Warming-up'];
const DOWNLOADABLE_VISUAL_SELECTOR = '.chart-card, .data-table-wrap';
const INTERACTIVE_VISUAL_CHILD_SELECTOR = 'a, button, details, input, select, summary, textarea';
const DOWNLOAD_BANNER_SELECTOR = '[data-merlin-visual-download-ui]';
const DOWNLOAD_ENHANCED_ATTR = 'data-merlin-download-enhanced';
const DOWNLOAD_STYLE_ID = 'merlin-visual-download-style';
const DONUT_ENHANCED_ATTR = 'data-merlin-donut-enhanced';
const DONUT_SVG_SELECTOR = '[data-merlin-donut-svg]';

function clampNumber(value: number, min: number, max: number) {
	return Math.min(Math.max(value, min), max);
}

// Resolves CSS variables/theme tokens (for example `var(--accent)`) into a concrete color value.
// Why: exported SVG/canvas output needs browser-computed colors, not unresolved CSS custom properties.
function resolveCssColor(element: HTMLElement, rawColor: string, fallback: string) {
	const color = rawColor.trim() || fallback;
	const probe = document.createElement('span');
	probe.style.color = color;
	probe.style.display = 'none';
	element.appendChild(probe);

	const resolved = window.getComputedStyle(probe).color;
	probe.remove();

	return resolved || fallback;
}

// Rebuilds CSS-only donut charts as SVG circles inside the supplied markdown container.
// Why: html2canvas handles explicit SVG geometry more reliably than pseudo-elements/CSS gradients
// when users download chart cards as PNGs.
function enhanceDonutCharts(container: HTMLElement) {
	const donuts = Array.from(container.querySelectorAll<HTMLElement>('.donut'));

	for (const donut of donuts) {
		const styles = window.getComputedStyle(donut);
		const pct = clampNumber(Number.parseFloat(styles.getPropertyValue('--pct')) || 0, 0, 100);
		const rawColor = styles.getPropertyValue('--color') || 'var(--accent)';
		const rawTrackColor = styles.getPropertyValue('--border-soft') || styles.getPropertyValue('--border') || '#eef0f2';
		const color = resolveCssColor(donut, rawColor, '#0d7377');
		const trackColor = resolveCssColor(donut, rawTrackColor, '#eef0f2');
		const signature = `${pct}|${color}|${trackColor}`;

		if (donut.getAttribute(DONUT_ENHANCED_ATTR) === signature) continue;

		donut.querySelectorAll(DONUT_SVG_SELECTOR).forEach((svg) => svg.remove());

		const size = 120;
		const stroke = 14;
		const radius = 52;
		const circumference = 2 * Math.PI * radius;
		const dashOffset = circumference * (1 - pct / 100);
		const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');

		svg.setAttribute('data-merlin-donut-svg', 'true');
		svg.setAttribute('class', 'donut-svg');
		svg.setAttribute('viewBox', `0 0 ${size} ${size}`);
		svg.setAttribute('aria-hidden', 'true');
		svg.setAttribute('focusable', 'false');

		const track = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
		track.setAttribute('class', 'donut-svg-track');
		track.setAttribute('cx', '60');
		track.setAttribute('cy', '60');
		track.setAttribute('r', String(radius));
		track.setAttribute('fill', 'none');
		track.setAttribute('stroke', trackColor);
		track.setAttribute('stroke-width', String(stroke));

		const progress = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
		progress.setAttribute('class', 'donut-svg-progress');
		progress.setAttribute('cx', '60');
		progress.setAttribute('cy', '60');
		progress.setAttribute('r', String(radius));
		progress.setAttribute('fill', 'none');
		progress.setAttribute('stroke', color);
		progress.setAttribute('stroke-width', String(stroke));
		progress.setAttribute('stroke-dasharray', String(circumference));
		progress.setAttribute('stroke-dashoffset', String(dashOffset));
		progress.setAttribute('transform', 'rotate(-90 60 60)');

		svg.append(track, progress);
		donut.prepend(svg);
		donut.setAttribute(DONUT_ENHANCED_ATTR, signature);
	}
}

// Injects the one shared stylesheet used by the hover download affordance on chart/table visuals.
// Why: ChatMarkdown can render many messages, so centralizing the styles and guarding by id avoids
// duplicated <style> tags while keeping the enhancement self-contained.
function ensureDownloadBannerStyles() {
	if (document.getElementById(DOWNLOAD_STYLE_ID)) return;

	const style = document.createElement('style');
	style.id = DOWNLOAD_STYLE_ID;
	style.textContent = `
		.merlin-download-enhanced {
			position: relative;
			cursor: pointer;
		}

		.merlin-download-enhanced,
		.merlin-download-enhanced:hover {
			opacity: 1 !important;
		}

		.merlin-visual-download-banner {
			position: absolute;
			top: 0.5rem;
			right: 0.5rem;
			z-index: 20;
			display: inline-flex;
			align-items: center;
			gap: 0.32rem;
			padding: 0.28rem 0.45rem;
			border: 1px solid rgba(148, 163, 184, 0.24);
			border-radius: 9999px;
			background: rgba(255, 255, 255, 0.82);
			box-shadow: 0 6px 16px rgba(15, 23, 42, 0.08);
			color: #1e293b;
			font-size: 0.68rem;
			font-weight: 600;
			line-height: 1;
			letter-spacing: 0.01em;
			pointer-events: none;
			opacity: 0;
			transform: translateY(-0.25rem) scale(0.98);
			transition: opacity 160ms ease, transform 160ms ease, border-color 160ms ease, box-shadow 160ms ease;
			backdrop-filter: blur(10px);
		}

		.merlin-download-enhanced:hover .merlin-visual-download-banner,
		.merlin-download-enhanced:focus-within .merlin-visual-download-banner {
			opacity: 1;
			transform: translateY(0) scale(1);
			border-color: rgba(59, 130, 246, 0.3);
			box-shadow: 0 8px 18px rgba(37, 99, 235, 0.1);
		}

		.merlin-visual-download-banner__icon {
			display: grid;
			place-items: center;
			width: 1rem;
			height: 1rem;
			border-radius: 9999px;
			background: rgba(37, 99, 235, 0.08);
			color: #2563eb;
			font-size: 0.68rem;
		}

		.merlin-visual-download-banner__badge {
			border-radius: 9999px;
			background: rgba(37, 99, 235, 0.06);
			padding: 0.12rem 0.28rem;
			color: #2563eb;
			font-size: 0.56rem;
			font-weight: 700;
			letter-spacing: 0.05em;
		}

		.dark .merlin-visual-download-banner {
			border-color: rgba(255, 255, 255, 0.1);
			background: rgba(15, 23, 42, 0.78);
			box-shadow: 0 8px 18px rgba(0, 0, 0, 0.2);
			color: #e2e8f0;
		}

		.dark .merlin-download-enhanced:hover .merlin-visual-download-banner,
		.dark .merlin-download-enhanced:focus-within .merlin-visual-download-banner {
			border-color: rgba(96, 165, 250, 0.3);
		}

		.dark .merlin-visual-download-banner__icon,
		.dark .merlin-visual-download-banner__badge {
			background: rgba(96, 165, 250, 0.16);
			color: #93c5fd;
		}
	`;
	document.head.appendChild(style);
}

// Adds a non-capturable "Download PNG" overlay to top-level chart cards and data tables.
// Why: generated markdown may contain nested visuals, so this only enhances the outermost visual
// blocks to give users one clear click target per downloadable artifact.
function enhanceDownloadableVisuals(container: HTMLElement) {
	ensureDownloadBannerStyles();

	const allVisuals = Array.from(container.querySelectorAll<HTMLElement>(DOWNLOADABLE_VISUAL_SELECTOR));
	const visuals = allVisuals.filter(
		(visual) => !allVisuals.some((otherVisual) => otherVisual !== visual && visual.contains(otherVisual))
	);

	for (const skippedVisual of allVisuals) {
		if (visuals.includes(skippedVisual)) continue;
		skippedVisual.removeAttribute(DOWNLOAD_ENHANCED_ATTR);
		skippedVisual.classList.remove('merlin-download-enhanced');
		skippedVisual.querySelectorAll(DOWNLOAD_BANNER_SELECTOR).forEach((banner) => banner.remove());
	}

	for (const visual of visuals) {
		if (visual.getAttribute(DOWNLOAD_ENHANCED_ATTR) === 'true') continue;

		visual.setAttribute(DOWNLOAD_ENHANCED_ATTR, 'true');
		visual.classList.add('merlin-download-enhanced');

		const banner = document.createElement('div');
		banner.className = 'merlin-visual-download-banner';
		banner.setAttribute('data-merlin-visual-download-ui', 'true');
		banner.setAttribute('aria-hidden', 'true');
		banner.innerHTML = `
			<span class="merlin-visual-download-banner__icon">↓</span>
			<span>Download</span>
			<span class="merlin-visual-download-banner__badge">PNG</span>
		`;

		visual.appendChild(banner);
	}
}

// Captures a rendered visual element and downloads it as a PNG.
// Why: users need to export individual charts/tables from AI responses; the function waits for fonts,
// normalizes donut SVGs, hides download chrome, and temporarily expands scrollable containers so the
// saved image contains the full visual rather than only the visible viewport.
async function downloadElementAsPng(element: HTMLElement) {
	await document.fonts?.ready;
	enhanceDonutCharts(element);
	const hiddenUi = Array.from(element.querySelectorAll<HTMLElement>(DOWNLOAD_BANNER_SELECTOR)).map((ui) => ({
		ui,
		display: ui.style.display,
	}));
	let originalOverflowStyles: Pick<
		CSSStyleDeclaration,
		'height' | 'overflow' | 'overflowX' | 'overflowY' | 'width'
	> | null = null;

	try {
		hiddenUi.forEach(({ ui }) => {
			ui.style.display = 'none';
		});

		const hasScrollableOverflow =
			element.scrollWidth > element.clientWidth + 1 || element.scrollHeight > element.clientHeight + 1;
		const captureWidth = element.scrollWidth;
		const captureHeight = element.scrollHeight;

		if (hasScrollableOverflow) {
			originalOverflowStyles = {
				height: element.style.height,
				overflow: element.style.overflow,
				overflowX: element.style.overflowX,
				overflowY: element.style.overflowY,
				width: element.style.width,
			};
			element.style.width = `${captureWidth}px`;
			element.style.height = `${captureHeight}px`;
			element.style.overflow = 'visible';
			element.style.overflowX = 'visible';
			element.style.overflowY = 'visible';
		}

		const captureBounds = hasScrollableOverflow
			? { width: captureWidth, height: captureHeight, windowWidth: captureWidth, windowHeight: captureHeight }
			: {};

		const canvas = await html2canvas(element, {
			backgroundColor: '#ffffff',
			scale: Math.min(window.devicePixelRatio || 1, 2),
			useCORS: true,
			...captureBounds,
		});

		const link = document.createElement('a');
		link.href = canvas.toDataURL('image/png');
		link.download = `merlin-visual-${new Date().toISOString().replace(/[:.]/g, '-')}.png`;
		document.body.appendChild(link);
		link.click();
		link.remove();
	} finally {
		if (originalOverflowStyles) {
			element.style.width = originalOverflowStyles.width;
			element.style.height = originalOverflowStyles.height;
			element.style.overflow = originalOverflowStyles.overflow;
			element.style.overflowX = originalOverflowStyles.overflowX;
			element.style.overflowY = originalOverflowStyles.overflowY;
		}

		hiddenUi.forEach(({ ui, display }) => {
			ui.style.display = display;
		});
	}
}

function pickUnique(count: number, max: number): Set<number> {
	const set = new Set<number>();
	const target = Math.min(count, max);
	while (set.size < target) {
		set.add(Math.floor(Math.random() * max));
	}
	return set;
}

function MatrixLoader({
	rows = 4,
	cols = 6,
	size = 6,
	gap = 3,
	activeCount = 6,
	interval = 120,
	className = '',
	activeColor = 'bg-blue-400',
}: MatrixLoaderProps) {
	const total = rows * cols;
	const cells = useMemo<number[]>(() => Array.from({ length: total }, (_, i) => i), [total]);
	const [active, setActive] = useState<Set<number>>(() => pickUnique(activeCount, total));

	useEffect(() => {
		const id = window.setInterval(() => {
			setActive(pickUnique(activeCount, total));
		}, interval);

		return () => window.clearInterval(id);
	}, [activeCount, interval, total]);

	return (
		<div
			className={`grid ${className}`}
			style={{
				gridTemplateColumns: `repeat(${cols}, ${size}px)`,
				gap: `${gap}px`,
			}}
			aria-hidden='true'
		>
			{cells.map((i) => (
				<div
					key={i}
					className={[
						'rounded-[2px] transition-all duration-200 ease-out',
						active.has(i) ? `${activeColor} opacity-100 scale-110` : 'bg-gray-100 scale-90 dark:bg-white/10',
					].join(' ')}
					style={{ width: size, height: size }}
				/>
			))}
		</div>
	);
}

function formatStatusLabel(label: string, maxLen = 88): string {
	const compact = label
		.replace(/\s+/g, ' ')
		.replace(/[\u2022\u00B7]+/g, '')
		.trim()
		.replace(/[\s.]+$/, '');

	if (!compact) return '';
	if (compact.length <= maxLen) return compact;
	return `${compact.slice(0, maxLen - 1).trimEnd()}…`;
}

function formatToolName(tagName: string): string {
	return tagName.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
}

/**
 * Extract ask_followup_question data from raw text.
 * Returns question text and parsed options array from XML:
 * <ask_followup_question>
 *   <question>...</question>
 *   <options>["option1", "option2"]</options>
 * </ask_followup_question>
 */
function extractAskQuestion(raw: string): AskQuestionData | null {
	const askQuestionRe = /<ask_followup_question>[\s\S]*?<\/ask_followup_question>/g;
	const match = askQuestionRe.exec(raw);
	if (!match) return null;

	const content = match[0];

	// Extract question
	const questionRe = /<question>([\s\S]*?)<\/question>/;
	const questionMatch = questionRe.exec(content);
	if (!questionMatch) return null;
	const question = questionMatch[1].trim();

	// Extract options (JSON array)
	const optionsRe = /<options>([\s\S]*?)<\/options>/;
	const optionsMatch = optionsRe.exec(content);
	if (!optionsMatch) return null;

	let options: string[] = [];
	try {
		options = JSON.parse(optionsMatch[1].trim());
		if (!Array.isArray(options)) options = [];
	} catch {
		options = [];
	}

	if (!question || options.length === 0) return null;

	return { question, options };
}

/**
 * @param {string} incomingText - The full growing string from your backend
 * @param {number} speedMs - ms between character updates (e.g. 10-30ms)
 * @param {() => void} onAnimationComplete - Callback when animation finishes
 * @param {boolean} instant - If true, skip animation and display text immediately
 */
const useSmoothStreaming = (incomingText: string, speedMs = 10, onAnimationComplete?: () => void, instant = false) => {
	const [displayedText, setDisplayedText] = useState('');
	const animationFrameRef = useRef<number>(0);
	const lastUpdateTimeRef = useRef<number>(0);
	const currentIndexRef = useRef<number>(0);
	const completedRef = useRef<boolean>(false);

	// When instant is true, immediately set the full text without animation
	if (instant && incomingText && displayedText !== incomingText) {
		setDisplayedText(incomingText);
		currentIndexRef.current = incomingText.length;
		completedRef.current = true;
		onAnimationComplete?.();
	}

	// Reset / resync when stream changes in a non-monotonic way (common when you strip tags)
	useEffect(() => {
		const safePrefix = incomingText.slice(0, displayedText.length);
		const diverged = displayedText.length > 0 && safePrefix !== displayedText;
		const shrank = incomingText.length < currentIndexRef.current;

		if (incomingText.length === 0 || diverged || shrank) {
			currentIndexRef.current = 0;
			lastUpdateTimeRef.current = 0;
			completedRef.current = false;
			setDisplayedText('');
		}
	}, [incomingText, displayedText]);

	// Cancel any pending animation frame when component unmounts
	useEffect(() => {
		return () => {
			if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
		};
	}, []);

	const updateText = (timestamp: number) => {
		if (!lastUpdateTimeRef.current) lastUpdateTimeRef.current = timestamp;

		const elapsed = timestamp - lastUpdateTimeRef.current;

		if (elapsed > speedMs) {
			const remaining = incomingText.length - currentIndexRef.current;
			const charsToAdd = Math.min(Math.floor(elapsed / speedMs), remaining);

			if (charsToAdd > 0) {
				const newText = incomingText.slice(0, currentIndexRef.current + charsToAdd);
				setDisplayedText(newText);
				currentIndexRef.current += charsToAdd;
				lastUpdateTimeRef.current = timestamp;
			}
		}

		if (currentIndexRef.current >= incomingText.length) {
			if (!completedRef.current && incomingText.length > 0) {
				completedRef.current = true;
				onAnimationComplete?.();
			}
			return;
		}

		animationFrameRef.current = requestAnimationFrame(updateText);
	};

	useEffect(() => {
		if (incomingText.length > 0) {
			completedRef.current = false;
			animationFrameRef.current = requestAnimationFrame(updateText);
		}

		return () => {
			if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [incomingText, speedMs, onAnimationComplete]);

	return displayedText;
};

/* -------------------- XML/meta parsing helpers -------------------- */

/**
 * Check if the raw message contains <attempt_completion> tag.
 * When this tag is present, we should treat all tasks as complete.
 */
function hasAttemptCompletion(raw: string): boolean {
	const re = /<attempt_completion>[\s\S]*?<\/attempt_completion>/g;
	if (re.test(raw)) return true;

	// Fallback: some model responses incorrectly emit a chat-response wrapper
	// instead of <attempt_completion>. Treat this as completion as well.
	const chatResponseDivRe = /<div\b[^>]*\bclass=(['"])\s*[^"']*\bchat-response\b[^"']*\1[^>]*>/i;
	return chatResponseDivRe.test(raw);
}

function parseLastTaskProgress(raw: string): TaskItem[] {
	const re = /<task_progress>\s*([\s\S]*?)\s*<\/task_progress>/g;
	let m: RegExpExecArray | null;
	let last: string | null = null;

	while ((m = re.exec(raw))) last = m[1];
	if (!last) return [];

	const items: TaskItem[] = [];
	for (const line of last.split('\n')) {
		const mm = line.match(/^\s*-\s*\[(x| )\]\s*(.+?)\s*$/i);
		if (mm) {
			const done = mm[1].toLowerCase() === 'x';
			items.push({ done, label: mm[2] });
		}
	}
	return items;
}

/**
 * Streaming-friendly: if <result> started but not closed yet, return the partial content.
 * If <result> never started, returns null.
 */
function extractResultStreaming(raw: string): string | null {
	const startTag = '<result>';
	const endTag = '</result>';

	const startIdx = raw.indexOf(startTag);
	if (startIdx === -1) return null;

	const start = startIdx + startTag.length;
	const endIdx = raw.indexOf(endTag, start);
	const slice = endIdx === -1 ? raw.slice(start) : raw.slice(start, endIdx);

	// keep streaming feel but avoid a huge initial blank line
	return slice.replace(/^\s*\n/, '');
}

/**
 * Extract the result from the most recent <attempt_completion> block.
 *
 * A single accumulated stream can contain multiple attempt_completion blocks
 * when the agent retries or emits a replacement final response. In that case,
 * only the last completion should be rendered. This stays streaming-friendly:
 * if the latest attempt block has started but its <result> has not appeared yet,
 * return an empty string so older completions are not shown as stale content.
 */
function extractLastAttemptCompletionResultStreaming(raw: string): string | null {
	const attemptStartTag = '<attempt_completion>';
	const attemptEndTag = '</attempt_completion>';
	const attemptStartIdx = raw.lastIndexOf(attemptStartTag);

	if (attemptStartIdx === -1) return null;

	const contentStart = attemptStartIdx + attemptStartTag.length;
	const contentEnd = raw.indexOf(attemptEndTag, contentStart);
	const latestAttempt = contentEnd === -1 ? raw.slice(contentStart) : raw.slice(contentStart, contentEnd);

	return extractResultStreaming(latestAttempt) ?? '';
}

/**
 * Fallback extractor for model outputs shaped like:
 * <div class="chat-response"> ...response html... </div>
 *
 * This is treated the same as <attempt_completion><result>...</result></attempt_completion>.
 */
function extractChatResponseDivStreaming(raw: string): string | null {
	const openRe = /<div\b[^>]*\bclass=(['"])\s*[^"']*\bchat-response\b[^"']*\1[^>]*>/i;
	const openMatch = openRe.exec(raw);
	if (!openMatch || openMatch.index == null) return null;
	const start = openMatch.index;

	// Find the matching closing </div> for the outer chat-response wrapper,
	// not just the first </div> (which would truncate nested HTML content).
	const divTagRe = /<\/?div\b[^>]*>/gi;
	divTagRe.lastIndex = openMatch.index;

	let depth = 0;
	let endIdx = -1;
	let m: RegExpExecArray | null;

	while ((m = divTagRe.exec(raw))) {
		const tag = m[0];
		const isClose = /^<\s*\//.test(tag);

		if (!isClose) depth += 1;
		else depth -= 1;

		if (depth === 0) {
			endIdx = m.index + m[0].length;
			break;
		}
	}

	const slice = endIdx === -1 ? raw.slice(start) : raw.slice(start, endIdx);

	return slice.replace(/^\s*\n/, '');
}

function isTagChar(ch: string) {
	return /[A-Za-z0-9_:-]/.test(ch);
}

function parseTagAt(raw: string, i: number) {
	// expects raw[i] === '<'
	const len = raw.length;
	let j = i + 1;
	if (j >= len) return null;

	const isClose = raw[j] === '/';
	if (isClose) j++;

	// tag name
	const nameStart = j;
	while (j < len && isTagChar(raw[j])) j++;
	const name = raw.slice(nameStart, j);

	const tagEnd = raw.indexOf('>', i);
	if (!name || tagEnd === -1) return null;

	// self-closing?
	const inside = raw.slice(i, tagEnd + 1);
	const selfClosing = /\/>\s*$/.test(inside);

	return { name, isClose, selfClosing, tagEnd };
}

/**
 * Remove top-level XML blocks like <get_tool> ... </get_tool>, <task_progress>...</task_progress>, etc.
 * Keeps anything that is plain text outside those blocks.
 * Respects fenced code blocks (```), leaving them untouched.
 */
function stripTopLevelXmlBlocks(raw: string): string {
	let out = '';
	let i = 0;
	let depth = 0;

	let skipping = false;
	let skipRoot = '';
	let skipEndDepth = 0; // depth level to return to when the skipped block closes

	let inFence = false;
	let lineStart = true;

	while (i < raw.length) {
		// fence detection at line start
		if (lineStart && raw.startsWith('```', i)) {
			inFence = !inFence;
			out += '```';
			i += 3;
			lineStart = false;
			continue;
		}

		const ch = raw[i];
		if (ch === '\n') lineStart = true;
		else if (lineStart && ch !== '\r') lineStart = false;

		if (inFence) {
			out += ch;
			i++;
			continue;
		}

		if (ch === '<') {
			const tag = parseTagAt(raw, i);
			if (!tag) {
				// malformed/incomplete tag -> treat as text
				if (!skipping) out += ch;
				i++;
				continue;
			}

			const { name, isClose, selfClosing, tagEnd } = tag;

			// Visible inline tags ALWAYS win (e.g. <result>, <ask_followup_question>)
			const isVisibleInline = META_INLINE_TAGS.has(name);

			// Meta blocks are always stripped, even if nested,
			// BUT never strip the visible inline tags even if someone also put them in META_BLOCK_TAGS.
			const isMetaBlock = !isVisibleInline && META_BLOCK_TAGS.has(name);

			// Start skipping:
			// 1) meta blocks at ANY depth
			// 2) unknown tool/other tags ONLY at top level (depth === 0), excluding visible inline tags
			if (!skipping && !isClose) {
				if (isMetaBlock) {
					if (!selfClosing) {
						skipping = true;
						skipRoot = name;
						skipEndDepth = depth; // return here when the block closes
					}
				} else if (depth === 0 && !isVisibleInline) {
					// top-level tool/other tag
					if (!selfClosing) {
						skipping = true;
						skipRoot = name;
						skipEndDepth = 0;
					}
				}
			}

			// depth bookkeeping
			if (!selfClosing) {
				if (!isClose) depth++;
				else depth = Math.max(0, depth - 1);
			}

			// End skipping when the skipped root closes back to the depth it started at
			if (skipping && isClose && name === skipRoot && depth === skipEndDepth) {
				skipping = false;
				skipRoot = '';
				out += '\n';
			}

			// Always remove tag tokens from output
			i = tagEnd + 1;
			continue;
		}

		if (!skipping) out += ch;
		i++;
	}

	return out.trim();
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
	return !!value && typeof value === 'object' && !Array.isArray(value);
}

function isLeakedResolverObject(value: unknown): boolean {
	if (!isPlainObject(value)) return false;

	const keys = Object.keys(value).sort();
	const expected = ['confidence', 'note', 'picked_ids'];
	if (keys.length !== expected.length) return false;
	for (let i = 0; i < expected.length; i++) {
		if (keys[i] !== expected[i]) return false;
	}

	const pickedIds = value.picked_ids;
	const confidence = value.confidence;
	const note = value.note;

	if (!Array.isArray(pickedIds)) return false;
	if (pickedIds.some((id) => typeof id !== 'number')) return false;
	if (!(confidence === null || typeof confidence === 'number')) return false;
	if (!(note === null || typeof note === 'string')) return false;

	return true;
}

function parseJsonObjectAt(raw: string, start: number): { end: number; value: unknown } | null {
	if (raw[start] !== '{') return null;

	let i = start;
	let depth = 0;
	let inString = false;
	let escaped = false;

	while (i < raw.length) {
		const ch = raw[i];

		if (inString) {
			if (escaped) escaped = false;
			else if (ch === '\\') escaped = true;
			else if (ch === '"') inString = false;
			i++;
			continue;
		}

		if (ch === '"') {
			inString = true;
			i++;
			continue;
		}

		if (ch === '{') depth++;
		if (ch === '}') {
			depth--;
			if (depth === 0) {
				const candidate = raw.slice(start, i + 1);
				try {
					return { end: i + 1, value: JSON.parse(candidate) };
				} catch {
					return null;
				}
			}
		}

		i++;
	}

	return null;
}

function stripLeakedResolverJsonArtifacts(raw: string): string {
	if (!raw.includes('picked_ids') || !raw.includes('confidence') || !raw.includes('note')) {
		return raw;
	}

	let out = '';
	let i = 0;

	while (i < raw.length) {
		if (raw[i] !== '{') {
			out += raw[i];
			i++;
			continue;
		}

		const parsed = parseJsonObjectAt(raw, i);
		if (parsed && isLeakedResolverObject(parsed.value)) {
			i = parsed.end;
			while (i < raw.length && /\s/.test(raw[i])) i++;
			continue;
		}

		out += raw[i];
		i++;
	}

	return out.replace(/\n{3,}/g, '\n\n');
}

/**
 * Derive tool events from top-level tags only.
 * A "tool call" is considered a top-level tag that is not meta (task_progress/attempt_completion/result).
 */
function deriveToolEvents(raw: string): ToolEvent[] {
	const events: ToolEvent[] = [];
	let depth = 0;

	const counts = new Map<string, number>();
	const tagRe = /<\/?([a-zA-Z_][\w:-]*)[^>]*>/g;

	let m: RegExpExecArray | null;
	while ((m = tagRe.exec(raw))) {
		const full = m[0];
		const name = m[1];
		const isClose = full.startsWith('</');
		const selfClosing = /\/>\s*$/.test(full);

		const isMeta = META_INLINE_TAGS.has(name) || META_BLOCK_TAGS.has(name);
		const isTopLevelOpen = !isClose && depth === 0;
		const isTopLevelClose = isClose && depth === 1;

		if (!isMeta && isTopLevelOpen) {
			const k = (counts.get(name) ?? 0) + 1;
			counts.set(name, k);
			events.push({ id: `${name}#${k}`, name, status: selfClosing ? 'done' : 'running' });
		}

		// update depth
		if (!selfClosing) {
			if (!isClose) depth++;
			else depth = Math.max(0, depth - 1);
		}

		if (!isMeta && isTopLevelClose) {
			// mark most recent running instance done
			for (let i = events.length - 1; i >= 0; i--) {
				if (events[i].name === name && events[i].status === 'running') {
					events[i].status = 'done';
					break;
				}
			}
		}
	}

	return events;
}

function parseAgentStream(raw: string) {
	const tasks = parseLastTaskProgress(raw);
	const tools = deriveToolEvents(raw);

	const result =
		extractLastAttemptCompletionResultStreaming(raw) ??
		extractResultStreaming(raw) ??
		extractChatResponseDivStreaming(raw);
	// Only recognized answer containers may render; stray text between tool calls is internal output.
	const unfilteredDisplayText = result ?? '';
	const displayText = stripLeakedResolverJsonArtifacts(unfilteredDisplayText);

	return { tasks, tools, displayText };
}

/* ---------- AskQuestionPicker component (ChatGPT-style) ---------- */
export function AskQuestionPicker({
	question,
	options,
	onSelect,
}: {
	question: string;
	options: string[];
	onSelect: (option: string) => void;
}) {
	return (
		<div className='mt-3 relative overflow-hidden rounded-xl border border-gray-200/70 bg-white/60 dark:border-white/10 dark:bg-gray-900/40 text-[0.9em]'>
			{/* Content */}
			<div className='p-4'>
				{/* Header with icon */}
				<div className='mb-3 flex items-center gap-2.5'>
					<div className='text-sm font-semibold text-gray-900 dark:text-gray-100'>Select an option</div>
				</div>

				{/* Question */}
				<div className='mb-4 text-sm text-gray-700 dark:text-gray-300'>{question}</div>

				{/* Options */}
				<div className='flex flex-col gap-2'>
					{options.map((option, idx) => (
						<button
							key={idx}
							type='button'
							onClick={() => onSelect(option)}
							className={[
								'group relative overflow-hidden rounded-lg border px-4 py-2 text-sm font-medium transition-all',
								'border-gray-200/70 bg-white hover:border-blue-300 hover:bg-blue-50',
								'dark:border-white/10 dark:bg-white/5 dark:hover:border-blue-500/30 dark:hover:bg-blue-500/10',
							].join(' ')}
						>
							{/* Button gradient on hover */}
							<div className='absolute inset-0 opacity-0 transition-opacity group-hover:opacity-100 bg-gradient-to-r from-blue-500/5 to-indigo-500/5 dark:from-blue-500/10 dark:to-indigo-500/10' />
							<span className='relative text-gray-700 dark:text-gray-200 group-hover:text-blue-700 dark:group-hover:text-blue-200'>
								{option}
							</span>
						</button>
					))}
				</div>
			</div>
		</div>
	);
}

type Props = {
	text: string;
	className?: string;
	fontSize?: FontSize;
	lineSpacing?: 'tight' | 'normal' | 'relaxed';
	letterSpacing?: 'tight' | 'normal' | 'wide';
	instant?: boolean;
	settled?: boolean;
	settledDelayMs?: number;
	onAnimationComplete?: () => void;
	onStatusChange?: (status: {
		doneCount: number;
		pct: number;
		hasTasks: boolean;
		hasTools: boolean;
		hasStatus: boolean;
		activeTaskIdx: number;
		anyToolRunning: boolean;
		isWorking: boolean;
		isSettled: boolean;
		tasks: Array<{ label: string; done: boolean }>;
		tools: Array<{ status: string }>;
		isAttemptComplete: boolean;
	}) => void;
	onAskQuestion?: (option: string, options: string[]) => void;
	onFacilitySwitch?: (facilityNameOrAlias: string) => void;
	showTaskProgress?: boolean;
	showToolFeed?: boolean;
	showQuestion?: boolean;
	maxToolEvents?: number;
	isLoading?: boolean;
	spinnerLabel?: string;
	doneSseReceived?: boolean;
	transportStatus?: 'idle' | 'queued' | 'reconnecting';
	retryAttempt?: number;
};

export function ChatMarkdown({
	text,
	className,
	fontSize = 'base',
	lineSpacing = 'tight',
	letterSpacing = 'normal',
	instant = false,
	settled = false,
	settledDelayMs = 1500,
	onAnimationComplete,
	onStatusChange,
	onAskQuestion,
	showTaskProgress = true,
	showToolFeed = true,
	showQuestion = true,
	maxToolEvents = 6,
	isLoading = false,
	spinnerLabel,
	doneSseReceived,
	transportStatus = 'idle',
	retryAttempt = 0,
}: Props) {
	const { tasks, tools, displayText } = useMemo(() => parseAgentStream(text), [text]);
	const askQuestionData = useMemo(() => extractAskQuestion(text), [text]);
	const [isExpanded, setIsExpanded] = useState(false);
	const contentRef = useRef<HTMLDivElement | null>(null);
	const [isSettled, setIsSettled] = useState(false);
	const [localSpinnerLabel, setLocalSpinnerLabel] = useState<string>(
		() => WAITING_SPINNER_LABELS[Math.floor(Math.random() * WAITING_SPINNER_LABELS.length)] ?? 'Preparing-task'
	);

	const rawDoneCount = tasks.filter((t) => t.done).length;

	const hasTasks = showTaskProgress && tasks.length > 0;
	const hasTools = showToolFeed && tools.length > 0;
	const isReconnecting = transportStatus === 'reconnecting' && retryAttempt > 0;
	const isQueueWaiting = transportStatus === 'queued';
	const isWaitingForTaskStart = isLoading && !hasTasks && !hasTools && !displayText.trim();
	const hasStatus = hasTasks || hasTools || isWaitingForTaskStart;

	const activeTaskIdx = hasTasks ? tasks.findIndex((t) => !t.done) : -1;
	const anyToolRunning = hasTools ? tools.some((t) => t.status !== 'done') : false;
	const isAttemptComplete = doneSseReceived ?? hasAttemptCompletion(text);
	const doneCount = isAttemptComplete && tasks.length > 0 ? tasks.length : rawDoneCount;
	const pct = tasks.length ? Math.round((doneCount / tasks.length) * 100) : 0;
	const isWorking =
		(!isAttemptComplete && (hasTasks || hasTools)) || anyToolRunning || (hasTasks && doneCount < tasks.length);
	const effectiveIsWorking = isWorking && !(settled && isSettled);

	const lineHeightClass =
		lineSpacing === 'tight' ? 'leading-snug' : lineSpacing === 'relaxed' ? 'leading-relaxed' : 'leading-normal';

	const letterSpacingClass =
		letterSpacing === 'tight' ? 'tracking-tight' : letterSpacing === 'wide' ? 'tracking-wide' : 'tracking-normal';

	const toolTail = tools.slice(Math.max(0, tools.length - maxToolEvents));
	const statusTitle = useMemo(() => {
		if (askQuestionData) return 'Question';
		if (isAttemptComplete && !effectiveIsWorking) return 'Complete';
		if (isReconnecting) return `Reconnecting… (attempt ${retryAttempt})`;
		if (isQueueWaiting) return spinnerLabel?.trim() || 'Queued';

		if (hasTasks && activeTaskIdx >= 0) {
			const activeTaskLabel = formatStatusLabel(tasks[activeTaskIdx]?.label ?? '');
			if (activeTaskLabel) return activeTaskLabel;
		}

		if (hasTasks && tasks.length > 0) {
			const latestTaskLabel = formatStatusLabel(tasks[tasks.length - 1]?.label ?? '');
			if (latestTaskLabel) return latestTaskLabel;
		}

		if (hasTools) {
			const runningTool = [...tools].reverse().find((t) => t.status === 'running');
			if (runningTool) {
				const toolName = formatStatusLabel(formatToolName(runningTool.name));
				return toolName ? `Running ${toolName}` : 'Running tool…';
			}
		}

		return 'Working…';
	}, [
		askQuestionData,
		effectiveIsWorking,
		isAttemptComplete,
		isReconnecting,
		retryAttempt,
		isQueueWaiting,
		spinnerLabel,
		hasTasks,
		activeTaskIdx,
		tasks,
		hasTools,
		tools,
	]);

	useEffect(() => {
		if (!isWaitingForTaskStart || spinnerLabel) return;

		const rotate = () => {
			setLocalSpinnerLabel((current) => {
				if (WAITING_SPINNER_LABELS.length <= 1) return current;
				let next = current;
				while (next === current) {
					next = WAITING_SPINNER_LABELS[Math.floor(Math.random() * WAITING_SPINNER_LABELS.length)] ?? current;
				}
				return next;
			});
		};

		const id = window.setInterval(rotate, 1800);
		return () => window.clearInterval(id);
	}, [isWaitingForTaskStart, spinnerLabel]);

	const waitingLabel = (spinnerLabel ?? localSpinnerLabel).replace(/-/g, ' ');
	const statusMeta = isWaitingForTaskStart
		? null
		: isReconnecting
		? `Recovery mode • attempt ${retryAttempt}`
		: isQueueWaiting
		? spinnerLabel?.trim() || 'Waiting in queue'
		: hasTasks
		? `${doneCount}/${tasks.length} • ${pct}%`
		: hasTools
		? `${toolTail.length} tool${toolTail.length === 1 ? '' : 's'}`
		: null;

	useEffect(() => {
		if (!settled) {
			setIsSettled(false);
			return;
		}

		setIsSettled(false);

		// "settled" can only become true after attempt completion.
		if (!isAttemptComplete) return;

		const timer = window.setTimeout(() => {
			setIsSettled(true);
		}, settledDelayMs);

		return () => window.clearTimeout(timer);
	}, [settled, settledDelayMs, text, isAttemptComplete]);

	useEffect(() => {
		onStatusChange?.({
			doneCount,
			pct,
			hasTasks,
			hasTools,
			hasStatus: hasTasks || hasTools,
			activeTaskIdx,
			anyToolRunning,
			isWorking: effectiveIsWorking,
			isSettled,
			tasks,
			tools,
			isAttemptComplete,
		});
	}, [
		doneCount,
		pct,
		hasTasks,
		hasTools,
		hasStatus,
		activeTaskIdx,
		anyToolRunning,
		effectiveIsWorking,
		isSettled,
		tasks,
		tools,
		isAttemptComplete,
		onStatusChange,
	]);

	// Since we're rendering HTML fragments, avoid character-by-character streaming.
	useEffect(() => {
		onAnimationComplete?.();
	}, [displayText, onAnimationComplete, instant]);

	// Support backend-rendered HTML action buttons (e.g. <div class="action-row"><button ...>...</button></div>)
	// and route clicks through the same ask-question submit flow.
	useEffect(() => {
		if (!onAskQuestion) return;
		const container = contentRef.current;
		if (!container) return;

		const onClick = (event: MouseEvent) => {
			const target = event.target as HTMLElement | null;
			if (!target) return;

			const button = target.closest('button');
			if (!button || !container.contains(button)) return;

			const selectedOption = (button.textContent ?? '').trim();
			if (!selectedOption) return;

			const actionRow = button.closest('.action-row');
			const options = actionRow
				? Array.from(actionRow.querySelectorAll('button'))
						.map((btn) => (btn.textContent ?? '').trim())
						.filter(Boolean)
				: [selectedOption];

			event.preventDefault();
			onAskQuestion(selectedOption, options);
		};

		container.addEventListener('click', onClick);
		return () => container.removeEventListener('click', onClick);
	}, [displayText, onAskQuestion]);

	// Progressive enhancement for agent-rendered visuals. The LLM only emits semantic HTML
	// wrappers; the frontend owns browser behavior like PNG export.
	useEffect(() => {
		const container = contentRef.current;
		if (!container) return;

		enhanceDonutCharts(container);
		enhanceDownloadableVisuals(container);

		const onClick = (event: MouseEvent) => {
			const target = event.target as HTMLElement | null;
			if (!target || target.closest(INTERACTIVE_VISUAL_CHILD_SELECTOR)) return;

			const visual = target.closest(DOWNLOADABLE_VISUAL_SELECTOR) as HTMLElement | null;
			if (!visual || !container.contains(visual) || visual.getAttribute(DOWNLOAD_ENHANCED_ATTR) !== 'true') return;

			event.preventDefault();
			event.stopPropagation();
			void downloadElementAsPng(visual);
		};

		container.addEventListener('click', onClick);
		return () => container.removeEventListener('click', onClick);
	}, [displayText]);

	return (
		<div className='space-y-3'>
			{hasStatus && (
				<div className='relative overflow-hidden text-[0.7em]'>
					{hasTasks && (
						<div className='pointer-events-none absolute inset-x-0 bottom-0 h-[2px] bg-gray-200/60 dark:bg-white/10'>
							<div
								className={[
									'h-full transition-[width] duration-500',
									effectiveIsWorking
										? 'bg-gradient-to-r from-blue-500 to-indigo-500'
										: 'bg-gradient-to-r from-emerald-500 to-teal-500',
								].join(' ')}
								style={{ width: `${pct}%` }}
							/>
						</div>
					)}

					<div className='flex items-center gap-3 px-3 py-2 pl-0'>
						{askQuestionData ? (
							<div className='grid h-7 w-7 place-items-center rounded-lg border border-purple-200/70 bg-purple-50 text-purple-700 dark:border-purple-500/20 dark:bg-purple-500/10 dark:text-purple-200'>
								?
							</div>
						) : isWaitingForTaskStart ? (
							<div className='grid h-8 w-8 place-items-center rounded-lg border border-blue-200/70 bg-blue-50 text-blue-700 dark:border-blue-500/20 dark:bg-blue-500/10 dark:text-blue-200'>
								<MatrixLoader rows={3} cols={3} activeCount={3} interval={200} size={4} />
							</div>
						) : (
							<div
								className={[
									'grid h-8 w-8 place-items-center rounded-lg border',
									isReconnecting
										? 'border-amber-200/70 bg-amber-50 text-amber-700 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200'
										: effectiveIsWorking
										? 'border-blue-200/70 bg-blue-50 text-blue-700 dark:border-blue-500/20 dark:bg-blue-500/10 dark:text-blue-200'
										: 'border-emerald-200/70 bg-emerald-50 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-200',
								].join(' ')}
							>
								{effectiveIsWorking ? <Loader2 className='h-4 w-4 animate-spin' /> : <CheckCircle2 className='h-4 w-4' />}
							</div>
						)}

						<div className='min-w-0 flex-1'>
							<div className='flex justify-between items-center gap-2'>
								<div className='truncate text-sm font-semibold text-gray-900 dark:text-gray-100'>
									{isWaitingForTaskStart ? (
										<span className='inline-flex items-end'>
											<span className='animate-pulse'>{waitingLabel}</span>
											<span className='inline-flex items-end ml-0.5 leading-none pb-[1px]'>
												<span className='animate-bounce [animation-delay:0ms]'>.</span>
												<span className='animate-bounce [animation-delay:150ms]'>.</span>
												<span className='animate-bounce [animation-delay:300ms]'>.</span>
											</span>
										</span>
									) : (
										statusTitle
									)}
								</div>

								<div className='flex items-center gap-1.5 truncate text-xs text-gray-500 dark:text-gray-400'>
									{isWaitingForTaskStart ? (
										<span className='inline-block w-14' aria-hidden='true' />
									) : isReconnecting ? (
										<span className='inline-flex items-center gap-1 text-amber-600 dark:text-amber-300'>
											<Loader2 className='h-3 w-3 animate-spin' />
											attempt {retryAttempt}
										</span>
									) : isQueueWaiting ? (
										<span className='inline-flex items-center gap-1'>
											<Loader2 className='h-3 w-3 animate-spin' />
											queued
										</span>
									) : hasTasks ? (
										<span className='inline-flex items-center gap-1'>
											<ListTodo className='h-3 w-3' />
											{statusMeta}
										</span>
									) : hasTools ? (
										<span className='inline-flex items-center gap-1'>
											<Wrench className='h-3 w-3' />
											{statusMeta}
										</span>
									) : null}
								</div>
							</div>
						</div>

						{!isWaitingForTaskStart && (
							<button
								type='button'
								onClick={() => setIsExpanded((v) => !v)}
								className='inline-flex h-8 w-8 items-center justify-center rounded-lg bg-white/60 text-gray-700 hover:bg-white dark:bg-white/5 dark:text-gray-200'
								aria-label={isExpanded ? 'Collapse status details' : 'Expand status details'}
							>
								{isExpanded ? <ChevronUp className='h-4 w-4' /> : <ChevronDown className='h-4 w-4' />}
							</button>
						)}
						{isWaitingForTaskStart && <div className='h-8 w-8' aria-hidden='true' />}
					</div>

					{isExpanded && (
						<div className='border-t border-gray-200/70 px-3 py-3 dark:border-white/10'>
							{(isReconnecting || isQueueWaiting) && (
								<div className='mb-3 rounded-lg border border-amber-200/70 bg-amber-50/80 px-3 py-2 text-xs text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200'>
									<div className='font-semibold'>
										{isReconnecting
											? `Connection recovery in progress (attempt ${retryAttempt})`
											: 'Waiting for queued work to resume'}
									</div>
									<div className='mt-1 opacity-80'>
										{isReconnecting
											? 'The task list below is the last known agent progress. The client is retrying the stream connection.'
											: 'The task list below is the last known agent progress while the response is queued.'}
									</div>
								</div>
							)}
							{hasTasks && (
								<div>
									<div className='mb-2 flex items-center justify-between'>
										<div className='text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400'>
											Tasks
										</div>
										<div className='text-xs text-gray-500 dark:text-gray-400'>
											{doneCount}/{tasks.length}
										</div>
									</div>

									<ul className='max-h-40 space-y-1.5 overflow-auto pr-1'>
										{tasks.map((t, idx) => {
											const isActive = idx === activeTaskIdx && !t.done;
											return (
												<li key={idx} className='flex items-center gap-2'>
													<div className='mt-0.5'>
														{t.done ? (
															<CheckCircle2 className='h-4 w-4 text-emerald-600 dark:text-emerald-400' />
														) : (
															<Circle
																className={[
																	'h-4 w-4',
																	isActive ? 'text-blue-600 dark:text-blue-300' : 'text-gray-300 dark:text-white/20',
																].join(' ')}
															/>
														)}
													</div>
													<div
														className={[
															'text-sm text-gray-800 dark:text-gray-100',
															t.done ? 'opacity-70 line-through decoration-gray-300 dark:decoration-white/20' : '',
														].join(' ')}
													>
														{t.label}
													</div>
												</li>
											);
										})}
									</ul>
								</div>
							)}
						</div>
					)}
				</div>
			)}

			<div
				ref={contentRef}
				className={[
					'antialiased text-slate-900 dark:text-slate-100',
					lineHeightClass,
					letterSpacingClass,
					'text-pretty',
					className ?? '',
				].join(' ')}
				style={{
					overflowWrap: 'break-word',
					wordBreak: 'normal',
					hyphens: 'auto',
					fontSize: FONT_SIZE_MAP[fontSize],
				}}
				dangerouslySetInnerHTML={{ __html: displayText }}
			/>

			{showQuestion && askQuestionData && onAskQuestion && (
				<AskQuestionPicker
					question={askQuestionData.question}
					options={askQuestionData.options}
					onSelect={(option) => onAskQuestion(option, askQuestionData.options)}
				/>
			)}
		</div>
	);
}

export default ChatMarkdown;
